/* Client-only motion: Lenis smooth scroll bridged to the GSAP ticker, and a
   transform-only reveal hook. Everything is reduced-motion gated and runs in
   effects only (SSR safe). The scroll-scrub engine owns its own media time;
   nothing here touches the video elements. */
import { useEffect, useRef, useState, type ReactNode } from "react";

export function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function SmoothScroll() {
  useEffect(() => {
    if (prefersReducedMotion()) return;
    if (window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    void Promise.all([import("lenis"), import("gsap")]).then(([lenisMod, gsapMod]) => {
      if (cancelled) return;
      const Lenis = lenisMod.default;
      const gsap = gsapMod.gsap;
      const lenis = new Lenis({ autoRaf: false, lerp: 0.13, smoothWheel: true });
      const tick = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(500, 33);
      cleanup = () => {
        gsap.ticker.remove(tick);
        lenis.destroy();
      };
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);
  return null;
}

/** Marks the element data-in="true" once it enters the viewport. The element
 * is visible from the first paint (transform-only offset), so headless
 * screenshots never see a blank section. */
export function useReveal<T extends HTMLElement>(margin = "-10% 0px", attr = "in") {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion()) {
      node.dataset[attr] = "true";
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            node.dataset[attr] = "true";
            io.disconnect();
          }
        }
      },
      { rootMargin: margin, threshold: 0.05 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [margin, attr]);
  return ref;
}

export function Reveal({
  children,
  className = "",
  axis,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  axis?: "x";
  as?: "div" | "section" | "article" | "header" | "p" | "h2" | "li";
}) {
  const ref = useReveal<HTMLElement>();
  const Tag = as as "div";
  return (
    <Tag ref={ref as never} className={`im-reveal${axis === "x" ? " im-reveal--x" : ""} ${className}`.trim()}>
      {children}
    </Tag>
  );
}

/** Counts up from 0 to the numeric part of `value` when in view. Non-numeric
 * suffixes (like "+") are preserved. Server renders the final value. */
export function CountUp({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const match = value.match(/^(\d+)(.*)$/);
    if (!match || prefersReducedMotion()) return;
    const target = Number(match[1]);
    const suffix = match[2];
    let frame = 0;
    let started = false;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting) || started) return;
      started = true;
      io.disconnect();
      const t0 = performance.now();
      const dur = 1400;
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        node.textContent = `${Math.round(target * eased)}${suffix}`;
        if (p < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    });
    io.observe(node);
    return () => {
      io.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value]);
  return <span ref={ref}>{value}</span>;
}
